/**
 * Native Authentication Service
 * Uses Capacitor Preferences API for secure token storage
 * and Bearer tokens in Authorization headers for API calls
 */

import { Preferences } from '@capacitor/preferences';
import { AuthService, AuthToken } from './types';
import { JWTLogic } from './logic';
import { UserLogic } from '@/modules/user';
import type { User } from '@/modules/user';
import { ENV } from '@/modules/config';
import { DEVICE_ID_HEADER, ensureDeviceId } from '@/util/deviceId';

export class NativeAuthService implements AuthService {
  private readonly TOKEN_KEY = 'auth_token';

  async getToken(): Promise<string | null> {
    try {
      const { value } = await Preferences.get({ key: this.TOKEN_KEY });
      if (!value) {
        return null;
      }

      const token: AuthToken = JSON.parse(value);

      // Check if token is expired using actual JWT expiration (not calculated time)
      const jwt = JWTLogic.decodeJwt(token.token);
      if (JWTLogic.isExpired(jwt)) {
        await this.clearToken();
        return null;
      }

      return token.token;
    } catch (error: unknown) {
      // eslint-disable-next-line no-console
      console.error('Failed to get stored token:', error);
      await this.clearToken();
      return null;
    }
  }

  async getCurrentUser(): Promise<User | null> {
    try {
      // Use fetch directly (not apiCall) to avoid circular dep: api.ts → logic.ts → NativeAuthService → api.ts.
      // Attach X-Device-ID explicitly (apiCall normally adds it) so /me resolves
      // per-device verification state instead of falling back to stale JWT claims (#283).
      const url = `${ENV.apiBaseUrl}/api/users/me`;
      const headers: Record<string, string> = {
        'X-Auth-Transport': ENV.authTransport,
        [DEVICE_ID_HEADER]: await ensureDeviceId(),
      };
      const token = await this.getToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch(url, { credentials: 'omit', headers });
      if (response.ok) {
        const data = await response.json();
        return UserLogic.fromNestedUserJSON(data);
      }
      if (response.status === 401 || response.status === 403) return null;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('Failed to fetch user from API:', error);
    }
    return null;
  }

  async isAuthenticated(): Promise<boolean> {
    const token = await this.getToken();
    return token !== null;
  }

  async refreshToken(): Promise<AuthToken | null> {
    // For JWT tokens, we don't refresh - they are stateless
    // The token will be validated on each request
    const token = await this.getToken();
    if (token) {
      const jwt = JWTLogic.decodeJwt(token);
      const jwtExpiration = JWTLogic.getExpiration(jwt);

      // Unverified (guest) tokens may have no expiration (indefinite), which is valid
      return {
        token,
        expiresAt: jwtExpiration,
      };
    }
    return null;
  }

  async setToken(token: AuthToken): Promise<void> {
    await Preferences.set({
      key: this.TOKEN_KEY,
      value: JSON.stringify(token),
    });
  }

  async clearToken(): Promise<void> {
    await Preferences.remove({ key: this.TOKEN_KEY });
  }

  async getBearerToken(): Promise<string | null> {
    return this.getToken();
  }
}
